import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtMoney, QUOTE_STATUS } from "../../lib/labels";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function NewQuoteDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ supplier_id: "", subtotal: "", validity: "", delivery_weeks: "", payment_terms: "30 días fecha factura", purchase_request_id: "", description: "" });
  const [busy, setBusy] = useState(false);
  const { data: suppliers } = useQuery({ queryKey: ["fin-suppliers-active"], queryFn: async () => (await api.get("/finanzas/suppliers", { params: { status: "activo" } })).data, staleTime: 60_000 });
  const { data: prs } = useQuery({ queryKey: ["fin-prs-mini"], queryFn: async () => (await api.get("/finanzas/purchase-requests")).data, staleTime: 60_000 });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finanzas/quotes", {
        ...form,
        subtotal: Number(form.subtotal),
        delivery_weeks: form.delivery_weeks ? Number(form.delivery_weeks) : undefined,
        validity: form.validity || undefined,
        purchase_request_id: form.purchase_request_id || undefined,
      });
      toast.success(`Oferta ${r.data.quote_number} registrada — pendiente de validación humana`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-quote-dialog">
        <DialogHeader><DialogTitle>Registrar oferta de proveedor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Proveedor *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="quote-supplier"><SelectValue placeholder="Selecciona proveedor…" /></SelectTrigger>
              <SelectContent>{(suppliers || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Base imponible (€) *</Label><Input type="number" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} data-testid="quote-subtotal" /></div>
            <div><Label>Válida hasta</Label><Input type="date" value={form.validity} onChange={(e) => setForm({ ...form, validity: e.target.value })} data-testid="quote-validity" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Plazo de entrega (semanas)</Label><Input type="number" value={form.delivery_weeks} onChange={(e) => setForm({ ...form, delivery_weeks: e.target.value })} data-testid="quote-weeks" /></div>
            <div><Label>Condiciones de pago</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="quote-terms" /></div>
          </div>
          <div>
            <Label>Relacionar con solicitud (opcional)</Label>
            <Select value={form.purchase_request_id || "none"} onValueChange={(v) => setForm({ ...form, purchase_request_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="quote-pr"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin solicitud</SelectItem>
                {(prs || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.pr_number} — {p.description.slice(0, 40)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="quote-description" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || !form.supplier_id || !Number(form.subtotal)} data-testid="quote-submit">
            {busy ? "Registrando…" : "Registrar oferta"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Ofertas() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const { data: items, refetch } = useQuery({
    queryKey: ["fin-quotes", status],
    queryFn: async () => (await api.get("/finanzas/quotes", { params: { status: status || undefined } })).data,
  });

  const act = async (q, action, payload = {}) => {
    try {
      await api.post(`/finanzas/quotes/${q.id}/action`, { action, ...payload });
      toast.success("Acción registrada");
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="ofertas-page">
      <PageHeader
        title="Ofertas de proveedores"
        subtitle="Recepción, extracción IA demo y validación humana antes de comparar."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-quote-button"><Plus className="mr-1 h-4 w-4" /> Registrar oferta</Button>}
      />
      <div className="mb-4">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(QUOTE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items && items.length === 0 && <EmptyState title="Sin ofertas" description="Registra la primera oferta de proveedor." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Oferta</TableHead><TableHead>Proveedor</TableHead><TableHead>Solicitud</TableHead>
                <TableHead>Validez</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-mono text-xs">{q.quote_number}</TableCell>
                  <TableCell className="text-sm">{q.supplier_name}</TableCell>
                  <TableCell className="font-mono text-xs">{q.purchase_request_id || "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(q.validity)}</TableCell>
                  <TableCell><Chip map={QUOTE_STATUS} value={q.status} testid={`quote-status-${q.id}`} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(q.total)}</TableCell>
                  <TableCell className="text-right">
                    {["recibida", "en_extraccion", "pendiente_validacion"].includes(q.status) && (
                      <Button size="sm" variant="outline" onClick={() => act(q, "validar")} data-testid={`quote-validate-${q.id}`}>Validar</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NewQuoteDialog open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); refetch(); qc.invalidateQueries({ queryKey: ["fin-prs"] }); }} />
    </div>
  );
}
